/**
 * Home Tab Products Section
 * Handles tab switching and swiper initialization for product sliders
 * Lazy rendering: only the first tab is rendered on page load,
 * other tabs are rendered on first click via <template> extraction.
 */

function initHomeTabProducts() {
  document.querySelectorAll('[data-home-tab-products]').forEach((section) => {
    if (section.dataset.homeTabProductsReady === 'true') return;
    section.dataset.homeTabProductsReady = 'true';

    const sectionId = section.getAttribute('data-home-tab-products');
    let tabs = Array.prototype.slice.call(section.querySelectorAll('[data-home-tab-btn]'));
    let panels = Array.prototype.slice.call(section.querySelectorAll('[data-home-tab-panel]'));
    const swipers = {};

    // 모바일 여부 확인 (터치 디바이스 감지)
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    function getTabSortOrder(element) {
      var value = Number(element.getAttribute('data-home-tab-order'));
      return Number.isFinite(value) ? value : 10;
    }

    function sortElementsByTabOrder(elements) {
      return elements
        .map(function(element, index) {
          return {
            element: element,
            index: index,
            order: getTabSortOrder(element)
          };
        })
        .sort(function(a, b) {
          if (a.order !== b.order) return a.order - b.order;
          return a.index - b.index;
        })
        .map(function(item) {
          return item.element;
        });
    }

    function applyTabOrder() {
      var tabsContainer = section.querySelector('[data-home-tab-tabs]');
      var panelsContainer = section.querySelector('[data-home-tab-content]');
      if (!tabsContainer || !panelsContainer) return;

      tabs = sortElementsByTabOrder(tabs);
      panels = sortElementsByTabOrder(panels);

      tabs.forEach(function(tab, index) {
        tab.setAttribute('data-tab-index', index);
        tabsContainer.appendChild(tab);
      });

      panels.forEach(function(panel, index) {
        panel.setAttribute('data-panel-index', index);
        panelsContainer.appendChild(panel);
      });
    }

    applyTabOrder();

    /**
     * <template data-lazy-content> 내용을 패널에 삽입
     * @returns {boolean} lazy content가 있었는지 여부
     */
    function materializeLazyContent(panel) {
      var tmpl = panel.querySelector('template[data-lazy-content]');
      if (!tmpl) return false;
      panel.appendChild(tmpl.content);
      tmpl.remove();
      bindButtonEffects(panel);
      return true;
    }

    function bindButtonEffects(context) {
      var root = context || section;
      var buttons = root.querySelectorAll('[data-btn-filler]');

      buttons.forEach(function(btn) {
        if (btn.dataset.homeTabButtonReady === 'true') return;
        btn.dataset.homeTabButtonReady = 'true';

        var handleInteraction = function() {
          this.classList.remove('hover-out');
          this.classList.add('hover-in');
          var el = this;
          setTimeout(function() {
            el.classList.remove('hover-in');
            el.classList.add('hover-out');
          }, 300);
        };

        btn.addEventListener('click', handleInteraction);
        btn.addEventListener('touchstart', handleInteraction, { passive: true });
      });
    }

    function normalizeMembersWeekLabel(value) {
      return (value || '').replace(/\s+/g, ' ').trim().toLowerCase();
    }

    function matchesMembersWeekGroup(element, detail) {
      var group = element.getAttribute('data-members-week-button-group') || 'primary';
      var customLabel = normalizeMembersWeekLabel(element.getAttribute('data-members-week-button-label'));
      var activeBrand = detail && detail.brand ? detail.brand : 'primary';
      var activeLabel = normalizeMembersWeekLabel(detail && detail.label);

      if (group === 'both') return activeBrand === 'primary';
      if (group === 'custom') return customLabel !== '' && customLabel === activeLabel;

      return group === activeBrand;
    }

    function getMembersWeekBrand(tab) {
      var group = tab.getAttribute('data-members-week-button-group') || 'primary';

      if (group === 'primary' || group === 'secondary') {
        return group;
      }

      return '';
    }

    function getActiveMembersWeekNavigationDetail() {
      var activeTab = document.querySelector('[data-members-week-navigation] [data-members-week-brand-tab].is-active');
      if (!activeTab) return null;

      return {
        brand: activeTab.getAttribute('data-members-week-brand') || 'primary',
        label: activeTab.getAttribute('data-members-week-button-label') || activeTab.textContent.replace(/\s+/g, ' ').trim()
      };
    }

    function notifyMembersWeekNavigation(tab) {
      var brand = getMembersWeekBrand(tab);
      if (!brand) return;

      document.dispatchEvent(new CustomEvent('members-week-products:tab-change', {
        detail: {
          brand: brand,
          label: tab.textContent.replace(/\s+/g, ' ').trim(),
          source: section,
          tab: tab
        }
      }));
    }

    function scrollActiveTabIntoView(tab) {
      if (!tab) return;
      var container = section.querySelector('[data-home-tab-tabs]');
      if (!container) return;
      // 가로 스크롤 가능할 때만 (overflow 없으면 불필요)
      if (container.scrollWidth <= container.clientWidth + 1) return;

      var cRect = container.getBoundingClientRect();
      var tRect = tab.getBoundingClientRect();
      // 활성 탭을 컨테이너 가운데로
      var target = container.scrollLeft + (tRect.left - cRect.left) - (container.clientWidth - tRect.width) / 2;
      var max = container.scrollWidth - container.clientWidth;
      if (target < 0) target = 0;
      if (target > max) target = max;
      container.scrollTo({ left: target, behavior: 'smooth' });
    }

    function activateTab(tab) {
      var targetId = tab.getAttribute('data-tab-target');

      tabs.forEach(function(t) {
        t.classList.toggle('active', t === tab);
      });

      scrollActiveTabIntoView(tab);

      panels.forEach(function(panel) {
        var panelId = panel.getAttribute('data-panel-id');
        if (panelId === targetId) {
          panel.classList.add('active');

          materializeLazyContent(panel);
          initPanelSwiper(panel);

          if (swipers[panelId]) {
            setTimeout(function() {
              swipers[panelId].update();
            }, 100);
          }
        } else {
          panel.classList.remove('active');
        }
      });
    }

    function applyMembersWeekFilter(detail) {
      var matchingTabs = [];

      tabs.forEach(function(tab) {
        var matches = matchesMembersWeekGroup(tab, detail);
        tab.hidden = !matches;
        tab.classList.toggle('members-week-filter-hidden', !matches);
        if (matches) matchingTabs.push(tab);
      });

      panels.forEach(function(panel) {
        var matches = matchesMembersWeekGroup(panel, detail);
        panel.hidden = !matches;
        panel.classList.toggle('members-week-filter-hidden', !matches);
        if (!matches) panel.classList.remove('active');
      });

      if (matchingTabs.length === 0) {
        tabs.forEach(function(tab) { tab.classList.remove('active'); });
        return;
      }

      var activeTab = matchingTabs.filter(function(tab) {
        return tab.classList.contains('active');
      })[0] || matchingTabs[0];

      activateTab(activeTab);
    }

    var initialMembersWeekDetail = window.FilaMembersWeekActiveProductGroup || getActiveMembersWeekNavigationDetail();
    if (initialMembersWeekDetail) {
      applyMembersWeekFilter(initialMembersWeekDetail);
    } else if (tabs[0]) {
      activateTab(tabs[0]);
    }

    /**
     * 패널에 Swiper 초기화 + PC 커스텀 드래그 + 모바일 링크 터치 바인딩
     */
    function initPanelSwiper(panel) {
      var panelId = panel.getAttribute('data-panel-id');
      if (swipers[panelId]) return;

      var swiperEl = panel.querySelector('[data-home-tab-swiper]');
      if (!swiperEl) return;

      var nextEl = panel.querySelector('[data-home-tab-next="' + panelId + '"]');
      var prevEl = panel.querySelector('[data-home-tab-prev="' + panelId + '"]');

      swipers[panelId] = new Swiper(swiperEl, {
        slidesPerView: 2.3,
        spaceBetween: 16,
        watchOverflow: true,
        simulateTouch: isTouchDevice,
        allowTouchMove: isTouchDevice,
        shortSwipes: true,
        longSwipesRatio: 0.3,
        touchStartPreventDefault: false,
        passiveListeners: true,
        threshold: 20,
        navigation: {
          nextEl: nextEl,
          prevEl: prevEl,
        },
        breakpoints: {
          750: {
            slidesPerView: 3,
            spaceBetween: 20,
          },
          990: {
            slidesPerView: 4,
            spaceBetween: 20,
          },
          1200: {
            slidesPerView: 4.1,
            spaceBetween: 20,
          },
        },
        on: {
          init: function() {
            updateButtonStates(this, nextEl, prevEl);
          },
          slideChange: function() {
            updateButtonStates(this, nextEl, prevEl);
          },
        },
      });

      // PC 커스텀 드래그 구현
      if (!isTouchDevice) {
        setupPCDrag(swipers[panelId], swiperEl);
      }

      // 모바일: Swiper 내부 터치 핸들링이 <a> 태그 click 합성을 방해하는 문제 우회
      // iOS Safari에서 touchmove 리스너가 있으면 click 이벤트가 간헐적으로 누락됨
      setupMobileLinkTouch(swiperEl);
    }

    /**
     * PC 커스텀 드래그 (터치 디바이스가 아닐 때)
     */
    function setupPCDrag(swiper, swiperEl) {
      var wrapper = swiperEl.querySelector('.swiper-wrapper');
      var isDragging = false;
      var startX = 0;
      var startTranslate = 0;
      var hasDragged = false;
      var clickPrevented = false;

      var handleMouseDown = function(e) {
        if (e.button !== 0) return;
        isDragging = true;
        hasDragged = false;
        clickPrevented = false;
        startX = e.clientX;
        startTranslate = swiper.getTranslate();
        wrapper.style.transition = 'none';
        swiperEl.style.cursor = 'grabbing';
        document.body.style.userSelect = 'none';
      };

      var handleMouseMove = function(e) {
        if (!isDragging) return;
        var diff = e.clientX - startX;
        if (Math.abs(diff) > 5) {
          if (!hasDragged) {
            hasDragged = true;
            clickPrevented = true;
            swiperEl.classList.add('is-dragging');
          }
          e.preventDefault();
          swiper.setTranslate(startTranslate + diff);
        }
      };

      var handleMouseUp = function() {
        if (!isDragging) return;
        isDragging = false;
        swiperEl.style.cursor = 'grab';
        wrapper.style.transition = '';
        document.body.style.userSelect = '';

        if (hasDragged) {
          swiper.slideToClosest();
        }

        setTimeout(function() {
          swiperEl.classList.remove('is-dragging');
          clickPrevented = false;
        }, 100);

        hasDragged = false;
      };

      swiperEl.querySelectorAll('img').forEach(function(img) {
        img.draggable = false;
      });
      swiperEl.querySelectorAll('a').forEach(function(link) {
        link.style.webkitUserDrag = 'none';
      });

      swiperEl.addEventListener('mousedown', handleMouseDown);
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      swiperEl.addEventListener('mouseleave', function() {
        if (isDragging) handleMouseUp();
      });
      swiperEl.style.cursor = 'grab';

      swiperEl.addEventListener('click', function(e) {
        if (clickPrevented || swiperEl.classList.contains('is-dragging')) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, true);
    }

    /**
     * 모바일: Swiper 내부 <a> 태그 터치 → 클릭 우회
     */
    function setupMobileLinkTouch(swiperEl) {
      swiperEl.querySelectorAll('a').forEach(function(link) {
        var linkTouchStartX = 0;
        var linkTouchStartY = 0;
        link.addEventListener('touchstart', function(e) {
          var touch = e.touches[0];
          linkTouchStartX = touch.clientX;
          linkTouchStartY = touch.clientY;
        }, { passive: true });

        link.addEventListener('touchend', function(e) {
          var touch = e.changedTouches[0];
          var dx = Math.abs(touch.clientX - linkTouchStartX);
          var dy = Math.abs(touch.clientY - linkTouchStartY);
          if (dx < 15 && dy < 15) {
            e.preventDefault();
            this.click();
          }
        });
      });
    }

    // 첫 번째 (active) 패널만 Swiper 초기화
    panels.forEach(function(panel) {
      if (panel.classList.contains('active')) {
        initPanelSwiper(panel);
      }
    });

    // Tab switching functionality
    tabs.forEach(function(tab) {
      tab.addEventListener('click', function() {
        activateTab(this);
        notifyMembersWeekNavigation(this);
      });

      // 모바일: 스크롤 컨테이너가 터치를 가로채는 문제 우회
      var touchStartX = 0;
      var touchStartY = 0;
      tab.addEventListener('touchstart', function(e) {
        var touch = e.touches[0];
        touchStartX = touch.clientX;
        touchStartY = touch.clientY;
      }, { passive: true });

      tab.addEventListener('touchend', function(e) {
        var touch = e.changedTouches[0];
        var dx = Math.abs(touch.clientX - touchStartX);
        var dy = Math.abs(touch.clientY - touchStartY);
        if (dx < 15 && dy < 15) {
          e.preventDefault();
          this.click();
        }
      });
    });

    // Function to update button states
    function updateButtonStates(swiper, nextEl, prevEl) {
      if (!nextEl || !prevEl) return;
      if (swiper.isBeginning) {
        prevEl.classList.add('swiper-button-disabled');
      } else {
        prevEl.classList.remove('swiper-button-disabled');
      }
      if (swiper.isEnd) {
        nextEl.classList.add('swiper-button-disabled');
      } else {
        nextEl.classList.remove('swiper-button-disabled');
      }
    }

    // dataLayer: main_click (product)
    section.addEventListener('click', function(e) {
      var viewAllButton = e.target.closest('[data-home-tab-view-all]');
      if (viewAllButton) {
        var panel = viewAllButton.closest('[data-home-tab-panel]');
        if (!panel) return;

        panel.querySelectorAll('.home-tab-products__product--hide-desktop, .home-tab-products__product--hide-mobile').forEach(function(item) {
          item.classList.remove('home-tab-products__product--hide-desktop');
          item.classList.remove('home-tab-products__product--hide-mobile');
        });

        viewAllButton.setAttribute('aria-expanded', 'true');
        if (viewAllButton.parentNode) {
          viewAllButton.parentNode.remove();
        }

        var panelId = panel.getAttribute('data-panel-id');
        if (swipers[panelId]) {
          swipers[panelId].update();
        }
        return;
      }

      if (typeof FilaDL === 'undefined') return;
      var card = e.target.closest('[data-dl-product-card]');
      if (!card) return;
      if (section.querySelector('.is-dragging')) return;

      var sectionName = section.getAttribute('data-dl-section-name') || 'Trending Now';
      var activeTab = section.querySelector('[data-home-tab-btn].active');
      var subBoxName = activeTab ? activeTab.textContent.trim() : '';

      FilaDL.push({
        event: 'main_click',
        click: {
          type: 'product',
          item_id: card.getAttribute('data-dl-item-id') || '',
          item_name: card.getAttribute('data-dl-item-name') || '',
          item_price: parseFloat(card.getAttribute('data-dl-item-price')) || 0,
          main_box_name: sectionName,
          sub_box_name: subBoxName,
          page_type: FilaDL.getMainPageType()
        }
      });
    });

    // Initialize button click/touch effects
    bindButtonEffects(section);

    document.addEventListener('members-week-navigation:change', function(event) {
      applyMembersWeekFilter(event.detail || {});
    });

    document.addEventListener('members-week:navigation-change', function(event) {
      applyMembersWeekFilter(event.detail || {});
    });
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initHomeTabProducts);
} else {
  initHomeTabProducts();
}

// Re-initialize on Shopify section load (for theme editor)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', function(event) {
    if (event.target.matches('[data-home-tab-products]') || event.target.querySelector('[data-home-tab-products]')) {
      initHomeTabProducts();
    }
  });
}
